import {
  type CSSProperties,
  type ExtractPropTypes,
  type PropType,
  Teleport,
  type TeleportProps,
  Transition,
  defineComponent,
  ref,
} from 'vue'

// Utils
import {
  createNamespace,
  extend,
  getZIndexStyle,
  isDef,
  numericProp,
  preventDefault,
  truthProp,
  unknownProp,
} from '@xzx-design/utils'

import { useEventListener, useLazyRender } from '@xzx-design/hooks'

const [name, bem] = createNamespace('overlay')

export type OverlayThemeVars = {
  overlayZIndex?: number | string
  overlayBackground?: string
}

export const overlayProps = {
  show: Boolean,
  zIndex: numericProp,
  duration: numericProp,
  className: unknownProp,
  lockScroll: truthProp,
  lazyRender: truthProp,
  customStyle: Object as PropType<CSSProperties>,
  teleport: [String, Object] as PropType<TeleportProps['to']>,
}

export type OverlayProps = ExtractPropTypes<typeof overlayProps>

export default defineComponent({
  name,

  props: overlayProps,

  setup(props, { slots }) {
    const root = ref<HTMLElement>()
    const lazyRender = useLazyRender(() => props.show || !props.lazyRender)

    const onTouchMove = (event: TouchEvent) => {
      if (props.lockScroll) {
        preventDefault(event, true)
      }
    }

    const renderOverlay = lazyRender(() => {
      const style: CSSProperties = extend(
        getZIndexStyle(props.zIndex),
        props.customStyle
      )

      if (isDef(props.duration)) {
        style.animationDuration = `${props.duration}s`
      }

      return (
        <div
          v-show={props.show}
          ref={root}
          style={style}
          class={[bem(), props.className]}
        >
          {slots.default?.()}
        </div>
      )
    })

    // useEventListener will set passive to `false` to eliminate the warning of Chrome
    useEventListener('touchmove', onTouchMove, {
      target: root,
    })

    return () => {
      const Content = (
        <Transition
          v-slots={{ default: renderOverlay }}
          name="xzx-fade"
          appear
        />
      )

      if (props.teleport) {
        return <Teleport to={props.teleport}>{Content}</Teleport>
      }

      return Content
    }
  },
})
